import React, { createContext, ReactNode, useContext, useState } from "react";

/**
 * Per-table UI state persisted in context, keyed by table name.
 * Used to keep search, sort, and pagination consistent across remounts.
 *
 * @property searchedText - Last submitted free-text search query
 * @property currentStartPage - 1-based active page number
 * @property currentSortableColumn - Identifier of the column currently being sorted (legacy)
 * @property startIndex - Inclusive start index of the visible row window
 * @property endIndex - Inclusive end index of the visible row window
 * @property sortAscending - When true the sort is ascending, otherwise descending
 * @property currentActiveArrayIndex - Index of the active page-buttons group (paginator UI)
 * @property lastBatchFetched - Highest 0-based page index successfully fetched (used for restore)
 * @property sortByKey - Row-data key driving the active sort
 * @property sortBySubKey - Optional nested key when the sort value lives under `row[key][subKey]`
 * @property sortByField - Canonical backend field name for the active sort
 *   (sourced from the matching `TableHeader.sortField`); intended for API calls
 * @property sortType - Hint for the comparator: "date" | "string" | "number"
 */
export type TableData = {
  [tableName: string]: {
    searchedText?: string;
    currentStartPage?: number;
    currentSortableColumn?: string;
    startIndex?: number;
    endIndex?: number;
    sortAscending?: boolean;
    currentActiveArrayIndex?: number;
    lastBatchFetched?: number;
    sortByKey?: string;
    sortBySubKey?: string;
    sortByField?: string;
    sortType?: "date" | "string" | "number";
  };
};

export type TablesContextType = {
  tablesData: TableData;
  getTableDataFromContext: (tableName: string) => TableData[string];
  setTableDataForContext: (
    tableName: string,
    newData: Partial<TableData[string]>,
  ) => void;
  resetTableDataForContext: () => void;
};

const TablesContext = createContext<TablesContextType>({
  tablesData: {},
  getTableDataFromContext: () => ({}),
  setTableDataForContext: () => {},
  resetTableDataForContext: () => {},
});

type TablesContextProviderProps = {
  children: ReactNode;
};

export const TablesContextProvider: React.FC<TablesContextProviderProps> = ({
  children,
}) => {
  const [tablesData, setTablesData] = useState<TableData>({});

  const getTableData = (tableName: string) => {
    return tablesData[tableName] || {};
  };

  const setTableData = (
    tableName: string,
    newData: Partial<TableData[string]>,
  ) => {
    setTablesData((prevTablesData) => {
      const updatedTablesData = {
        ...prevTablesData,
        [tableName]: {
          ...prevTablesData[tableName],
          ...newData,
        },
      };

      return updatedTablesData;
    });
  };

  const resetTableData = () => {
    setTablesData({});
  };

  return (
    <TablesContext.Provider
      value={{
        tablesData,
        getTableDataFromContext: getTableData,
        setTableDataForContext: setTableData,
        resetTableDataForContext: resetTableData,
      }}
    >
      {children}
    </TablesContext.Provider>
  );
};

export const useTablesContext = () => useContext(TablesContext);
